import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { z } from 'zod';

const apiErrorSchema = z
  .object({
    statusCode: z.number().int(),
    data: z.object({ error: z.unknown().optional() }).passthrough(),
  })
  .passthrough();

const structuredErrorSchema = z.object({ message: z.string().optional(), error: z.string().optional() }).passthrough();

@Injectable({ providedIn: 'root' })
export class HandleErrorsService {
  private snackbar = inject(MatSnackBar);
  private router = inject(Router);
  protected translateService = inject(TranslateService);

  handleErrors(error: unknown, redirect?: boolean) {
    const parsed = apiErrorSchema.safeParse(error);
    const err: z.infer<typeof apiErrorSchema> = parsed.success ? parsed.data : { statusCode: 0, data: {} };

    switch (true) {
      case err.statusCode >= 500 && err.statusCode <= 511:
        this.translateService.get('server-error').subscribe(message => {
          this.snackbar.open(message, this.okLabel(), { duration: 7000 });
        });
        break;
      case err.statusCode === 404 && !redirect:
        this.translateService.get('resource-not-found').subscribe(message => {
          this.snackbar.open(message, this.okLabel(), { duration: 7000 });
        });
        break;
      case err.statusCode === 404 && redirect:
        this.translateService.get('page-not-found').subscribe(message => {
          this.snackbar.open(message, this.okLabel(), { duration: 7000 });
        });
        this.router.navigate([{ outlets: { dialog: null, primary: ['error-404'] } }]);
        break;
      case err.statusCode === 401:
        this.translateService.get('no-auth').subscribe(message => {
          this.snackbar.open(message, this.okLabel(), { duration: 7000 });
        });
        break;
      case err.statusCode === 403:
        this.translateService.get('no-acces').subscribe(message => {
          this.snackbar.open(message, this.okLabel(), { duration: 7000 });
        });
        break;
      case err.statusCode === 409: {
        const raw = err.data?.error;
        const structured = structuredErrorSchema.safeParse(raw);
        const message =
          typeof raw === 'string'
            ? this.tryParseMessage(raw)
            : structured.success
              ? (structured.data.message ?? structured.data.error ?? '')
              : '';
        this.translateService.get('conflict-error', { statusCode: err.statusCode, message }).subscribe(translatedMessage => {
          this.snackbar.open(translatedMessage, this.okLabel(), { duration: 7000 });
        });
        break;
      }
      default:
        this.translateService.get('client-error').subscribe(message => {
          this.snackbar.open(message + ' ' + err.statusCode, this.okLabel(), { duration: 7000 });
        });
        break;
    }
  }

  /** Extracts a `.message` from a raw JSON string body, falling back to the raw text when it isn't JSON. */
  private tryParseMessage(raw: string): string {
    try {
      const parsed = structuredErrorSchema.safeParse(JSON.parse(raw));

      return parsed.success ? (parsed.data.message ?? raw) : raw;
    } catch {
      return raw;
    }
  }

  private okLabel(): string {
    return this.translateService.instant('ok');
  }
}
